<script lang="ts">
    import { Strings } from '@/data/constants';
    import { taskChoreMap, taskMap } from '@/data/tasks';
    import { QuestStatus } from '@/enums/quest-status';
    import { uiIcons } from '@/shared/icons';
    import { settingsState } from '@/shared/state/settings.svelte';
    import { DbResetType } from '@/shared/stores/db/enums';
    import { timeState } from '@/shared/state/time.svelte';
    import { userState } from '@/user-home/state/user';
    import { toNiceDuration } from '@/utils/formatting';
    import { getPercentStatusClass } from '@/utils/get-percent-class';
    import type { Chore } from '@/types/tasks';
    import type { CharacterChore } from '@/user-home/state/user/types/tasks.svelte';

    import IconifyWrapper from '@/shared/components/images/IconifyWrapper.svelte';
    import ParsedText from '@/shared/components/parsed-text/ParsedText.svelte';

    type Props = {
        characterId: number;
        fullTaskName: string;
    };
    let { characterId, fullTaskName }: Props = $props();

    let [taskName, choreName] = $derived(fullTaskName.split('|', 2));
    let character = $derived(userState.general.characterById[characterId]);
    let task = $derived(taskMap[taskName] || settingsState.customTaskMap[taskName]);
    let charTask = $derived(userState.activeViewTasks[character?.id]?.[taskName]);
    let charChore = $derived(
        userState.activeViewTasks[character?.id]?.[fullTaskName]?.chores?.[choreName]
    );

    let choreSets = $derived.by(() => {
        const ret: [Chore, CharacterChore][][] = [];

        if (charChore) {
            ret.push([[taskChoreMap[`${taskName}_${charChore.key}`], charChore]]);
        } else if (charTask) {
            let currentSet: (typeof ret)[0] = [];
            for (const chore of charTask.task.chores) {
                if (!chore) {
                    ret.push(currentSet);
                    currentSet = [];
                } else {
                    const charTaskChore = charTask.chores[chore.key];
                    if (charTaskChore && !charTaskChore.skipped) {
                        currentSet.push([chore, charTaskChore]);
                    }
                }
            }
            ret.push(currentSet);
        }

        if (task.sortByExpiry) {
            for (const choreSet of ret) {
                choreSet.sort((a, b) => {
                    return a[1].expiresAt.diff(b[1].expiresAt).toMillis();
                });
            }
        }

        return ret;
    });

    let anyErrors = $derived(
        choreSets.some((taskSet) =>
            taskSet.some(
                ([, charChore]) =>
                    !!charChore &&
                    (charChore.status === QuestStatus.NotStarted ||
                        charChore.status === QuestStatus.Error) &&
                    charChore.name !== '' &&
                    charChore.statusTexts.some((st) => !!st)
            )
        )
    );

    const getFixedText = function (text: string): [string, string] {
        text = text.replace(/\[\[tier(\d)\]\]/, '{craftedQuality:$1}');

        let cls = '';
        const m = text.match(/^(\d+)\/(\d+) /);
        if (m) {
            const have = parseInt(m[1]);
            const total = parseInt(m[2]);
            const per = (have / total) * 100;
            if (per === 100) {
                cls = 'status-success';
            } else if (per > 0) {
                cls = 'status-shrug';
            }
        }
        return [text, cls];
    };
</script>

<style lang="scss">
    table {
        &:not(:last-child) {
            border-bottom: 1px solid var(--border-color);
        }

        + table {
            border-top: 1px solid var(--border-color);
            margin-top: 0.5rem;
        }
    }
    td {
        padding-top: 0.1rem;
        padding-bottom: 0.2rem;
    }
    .name {
        --image-border-width: 1px;
        --scale: 1.2;

        max-width: 15rem;
        min-width: 11rem;
        text-align: left;
        white-space: nowrap;
    }
    .status {
        --scale: 1;

        //padding-left: 0;
        //padding-right: 0;
        text-align: center;
        white-space: nowrap;
        width: 1rem;
    }
    .error-text {
        font-size: 0.95rem;
        text-align: left;
        max-width: 7rem;
    }
    .status-text {
        color: #afffff;
        font-size: 0.95rem;
        padding-left: 0.7rem;
        text-align: left;

        :global(svg) {
            margin-left: -0.5rem;
        }
    }
    .skipped {
        opacity: 0.7;
    }
</style>

<div class="wowthing-tooltip">
    <h4>{character?.name || `Unknown Character #${characterId}`}</h4>
    <h5>{task?.name || `Unknown Task "${taskName}"`}</h5>

    {#each choreSets.filter((s) => s.length > 0) as choreSet (choreSet)}
        <table class="table-striped">
            <tbody>
                {#each choreSet as [chore, charTaskChore] (charTaskChore)}
                    {#if chore}
                        <tr
                            class:skipped={!charChore &&
                                charTaskChore.skipped &&
                                charTaskChore.status !== QuestStatus.Error}
                        >
                            <td
                                class="name text-overflow"
                                class:status-shrug={charTaskChore.status === QuestStatus.Error}
                            >
                                {#if chore.questReset === DbResetType.Daily && settingsState.value.tasks.showDailyIcon}
                                    <IconifyWrapper icon={uiIcons.squareD} cls="quality3" />
                                {:else if chore.questReset === DbResetType.Weekly && settingsState.value.tasks.showWeeklyIcon}
                                    <IconifyWrapper icon={uiIcons.squareW} cls="quality3" />
                                {/if}
                                {#if chore.accountWide && settingsState.value.tasks.showAccountIcon}
                                    <IconifyWrapper icon={uiIcons.squareA} cls="status-shrug" />
                                {/if}

                                {#if task.showExpiry}
                                    {@const remaining = charTaskChore.expiresAt
                                        .diff(timeState.slowTime)
                                        .toMillis()}
                                    <code>[{toNiceDuration(remaining, { useNbsp: false })}]</code>
                                {/if}

                                <ParsedText text={charTaskChore.name} />
                            </td>
                            <td class="status">
                                <span
                                    class="status-{['fail', 'shrug', 'success', 'fail'][
                                        charTaskChore.status
                                    ]}"
                                >
                                    {#if chore?.icon && !chore.noTooltipIcon}
                                        {#if typeof chore.icon === 'string'}
                                            <ParsedText text={chore.icon} />
                                        {:else}
                                            <IconifyWrapper icon={chore.icon} />
                                        {/if}
                                    {:else}
                                        <IconifyWrapper
                                            icon={[
                                                uiIcons.starEmpty,
                                                uiIcons.starHalf,
                                                uiIcons.starFull,
                                                uiIcons.lock,
                                            ][charTaskChore.status]}
                                        />
                                    {/if}
                                </span>
                            </td>
                            {#if anyErrors}
                                <td class="error-text">
                                    {#if charTaskChore.status === QuestStatus.Error}
                                        {charTaskChore.statusTexts[0]}
                                    {/if}
                                </td>
                            {:else if chore.questCount > 1}
                                {@const cls =
                                    charTaskChore.statusTexts[0] === Strings.doUnlockQuests
                                        ? 'status-fail'
                                        : getPercentStatusClass(
                                              ((chore.alwaysStarted
                                                  ? Math.max(1, charTaskChore.progressCurrent)
                                                  : charTaskChore.progressCurrent) /
                                                  charTaskChore.progressTotal) *
                                                  100
                                          )}
                                <td class="error-text">
                                    <ParsedText
                                        text={`[|${cls}|${charTaskChore.progressCurrent}/${charTaskChore.progressTotal}]`}
                                    />
                                </td>
                            {/if}
                        </tr>

                        {#if charTaskChore.status === QuestStatus.InProgress && charTaskChore.statusTexts[0]}
                            <tr class:skipped={charTaskChore.skipped}>
                                <td class="status-text" colspan={anyErrors ? 3 : 2}>
                                    {#each charTaskChore.statusTexts as statusText}
                                        {@const [fixedText, textClass] = getFixedText(statusText)}
                                        <div>
                                            {#if !statusText.startsWith('<')}
                                                &ndash;
                                            {/if}
                                            <ParsedText cls={textClass} text={fixedText} />
                                        </div>
                                    {/each}
                                </td>
                            </tr>
                        {/if}
                    {/if}
                {/each}
            </tbody>
        </table>
    {/each}

    {#if settingsState.value.tasks.showIconLegend}
        <div class="bottom" style:--scale="1.2">
            <span>
                <IconifyWrapper icon={uiIcons.squareD} cls="quality3" /> Daily
            </span>
            <span>
                <IconifyWrapper icon={uiIcons.squareW} cls="quality3" /> Weekly
            </span>
            <span>
                <IconifyWrapper icon={uiIcons.squareA} cls="status-shrug" /> Account
            </span>
        </div>
    {/if}
</div>
